import { registerSharedDependency } from 'core-components/helpers/MFHelper'
import settings from 'src/core/config/hostConfig'
import { getAppId, getAppOwnerId } from 'src/core/classes/helpers/getAppId'
import {
  clearTokens,
  getIdentityTokens,
  getTokens,
  setTokens,
} from 'src/core/classes/helpers/StorageHelper'
import { Rest } from 'src/core/classes/http'
import { wsConnectionPool } from 'src/core/classes/ws/WSConnectionPool'
import Logger from 'src/core/utils/Logger'
import { getTokenExpirationTime } from 'src/core/utils/Token'

import { processAuthRedirect } from '../helpers/authorization'
import { proxy, proxyIdentity } from '../helpers/UrlHelper'
import { TAuthService } from '../models/IAuth'

import { TokenRefresher } from './auth/TokerRefresher'

const TIME_FOR_REFRESH_TOKEN = 1000 * 60 * 2 // 2 min
const TIME_CHECK_TOKEN = 1000 * 60 * 0.5 // 0.5 min

class AuthService {
  private isInit = false
  private isTabActive = false
  private tokenRefresher = new TokenRefresher()
  private identityCheckPromise: Promise<string | null> | null = null
  private tokenCheckPromise: Promise<string | null> | null = null

  public init() {
    if (!this.isInit) {
      this.isInit = true
      const [isIdentity] = getIdentityTokens()
      if (window) {
        window.addEventListener('focus', () => {
          this.isTabActive = true
          isIdentity ? this.checkIdentityToken() : this.checkToken()
        })
        window.addEventListener('blur', () => {
          this.isTabActive = false
        })
        setInterval(() => {
          isIdentity ? this.checkIdentityToken() : this.checkToken()
        }, TIME_CHECK_TOKEN)
      }
    }
  }
  public checkToken = async (force: boolean = false) => {
    if (!this.tokenCheckPromise) {
      if (this.isTabActive || force) {
        this.tokenCheckPromise = new Promise(async (resolve) => {
          const [accessToken] = getTokens()
          const newToken = await this.checkTokenHandler(accessToken, false)
          this.tokenCheckPromise = null
          resolve(newToken)
        })
      } else {
        return null
      }
    }

    return this.tokenCheckPromise
  }

  public checkIdentityToken = async () => {
    if (!this.identityCheckPromise) {
      this.identityCheckPromise = new Promise(async (resolve) => {
        const [identityToken] = getIdentityTokens()
        const [accessToken] = getTokens()
        const newToken = await this.checkTokenHandler(identityToken || accessToken, true)

        if (newToken) {
          await this.exchangeIdentityToken(newToken)
        }

        this.identityCheckPromise = null
        resolve(newToken)
      })
    }

    return this.identityCheckPromise
  }

  private checkTokenHandler = async (token: string | null, isIdentity: boolean) => {
    const tokenExpTime = getTokenExpirationTime(token || '')
    if (tokenExpTime !== null) {
      if (tokenExpTime < TIME_FOR_REFRESH_TOKEN) {
        const resultToken = await this.refreshToken()
        if (resultToken && !isIdentity) {
          await wsConnectionPool.updateAuthToken()
        }
        return resultToken
      }
    }
    return ''
  }

  /**
   * @deprecated HP ID v2 is gone (the login is in auth/hpidV3). Kept as a stub for federated
   * remotes on an older core (checkEngine on release_25.11, kbase and docflow on
   * release_26.04): their logout calls it on the shared authService. Returning nothing makes
   * them log out locally. Remove once every remote runs this core.
   */
  public getAuthorizationData = async (): Promise<undefined> => undefined

  /**
   * @deprecated See getAuthorizationData. A login started from an old remote gets null, and
   * that remote shows its own "Login failed" error.
   */
  public fetchAuthorizeUrl = async (): Promise<null> => null

  public fetchIdentityAuthorizeUrl = async (
    type: TAuthService,
    currentPagePath?: string
  ): Promise<string | null> => {
    return Rest.post({
      url: proxyIdentity(`/authentication/${type}/url`),
      payload: {
        redirectUri: window.location.origin + `/authentication/${type}/callback`,
        pathname: currentPagePath,
      },
    })
  }

  public authorizeByPassport = async ({
    email,
    password,
    redirectUrl = '/',
  }: {
    email: string
    password: string
    redirectUrl?: string
  }): Promise<boolean> => {
    const url = proxy(settings.passportEndpoint)
    const payload = {
      AppId: getAppId(),
      UserName: email,
      Password: password,
    }
    const response = await this.getTokenData(url, payload)

    if (response && response.AccessToken && response.RefreshToken) {
      setTokens(getAppOwnerId(), response.AccessToken, response.RefreshToken)
      processAuthRedirect(redirectUrl)
      return true
    }
    return false
  }

  private getTokenData = async (url: string, payload: object) => {
    try {
      return await Rest.post({ url, payload })
    } catch (err: unknown) {
      Logger.error(err)
    }

    return { AccessToken: '', RefreshToken: '' }
  }

  public getOneTimeToken = async () => {
    const url = proxy(`/mbe/api/v2/onetime/token/${getAppId()}`)
    return await Rest.post({ url })
  }

  public hasToken = () => {
    const [accessToken, refreshToken] = getTokens()
    const [accessTokenNew, refreshTokenNew] = getIdentityTokens()
    return Boolean(accessToken && refreshToken) || Boolean(accessTokenNew && refreshTokenNew)
  }

  public isTokenRefreshing = (): boolean => {
    return this.isInit ? this.tokenRefresher.isRefreshing() : false
  }

  public refreshToken = async (): Promise<string | null> => {
    if (this.isInit) {
      const [isIdentity] = getIdentityTokens()
      const accessToken = await this.tokenRefresher.refreshToken(!!isIdentity)
      if (isIdentity && accessToken) {
        await this.exchangeIdentityToken(accessToken)
      }
      return accessToken
    }
    return null
  }

  public clearToken = () => {
    clearTokens()
  }

  public exchangeIdentityToken = async (accessToken: string) => {
    // Single identity service mode (e.g. stage): there is no app-designer to exchange the
    // identity token with, so the exchange becomes a no-op. Legacy/dev paths keep exchanging.
    const ignoreExchange = settings.notExchangeToken
    if (ignoreExchange) {
      return ''
    }

    try {
      const oldTokens = await Rest.post({
        url: proxy('/mbe/api/v2/access/token/for/sb'),
        payload: {
          AccessToken: accessToken,
        },
        force: true,
      })

      if (!oldTokens?.AccessToken || !oldTokens?.RefreshToken) {
        throw new Error('Empty exchange token request response')
      }
      setTokens(getAppOwnerId(), oldTokens.AccessToken, oldTokens.RefreshToken)
      await wsConnectionPool.updateAuthToken()
      return oldTokens.AccessToken
    } catch {
      return ''
    }
  }
}

export const authService = registerSharedDependency<AuthService>(
  'authService',
  () => new AuthService()
)
